import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import environment from '@/environment'
import { beforeSendSentryEvent } from '@/lib/sentryEvents'
import { beforeSendSentryLog } from '@/lib/sentryLogs'
import { shouldSampleReplayForError } from '@/lib/sentryReplay'

const sentryEnvironment = `${environment.isProd ? 'production' : environment.isDev ? 'development' : 'staging'}${
  import.meta.env.VITE_NATIVE_BUILD === 'true' ? '-native' : ''
}`

function buildSentryOptions() {
  return {
    dsn: 'https://676ec9f23a61962b3daa037e133169a0@o4508680792702976.ingest.de.sentry.io/4510097213816912',
    environment: sentryEnvironment,
    sendDefaultPii: true,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        beforeErrorSampling: shouldSampleReplayForError,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['error'] }),
    ],
    tracePropagationTargets: ['localhost', environment.apiBaseUrl].filter(Boolean),
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    beforeSend: beforeSendSentryEvent,
    beforeSendLog: beforeSendSentryLog,
    enabled: true,
  }
}

export async function initSentry(): Promise<void> {
  if (environment.isDev) return
  const options = buildSentryOptions()
  if (import.meta.env.VITE_NATIVE_BUILD === 'true') {
    const { init } = await import('@sentry/capacitor')
    init(options, Sentry.init)
    return
  }
  Sentry.init(options)
}
